import ModeButton from 'components/Buttons/ModeButton';
import BaseCard from 'components/Cards/BaseCard';
import type { ComponentType } from 'react';
import { Form } from 'react-bootstrap';
import { useGetStageSimScenariosQuery } from 'services/devices/devices.api';
import FloodSettingsField from '../../components/FloodSettingsField';
import DonorModeSection from './components/DonorModeSection';
import DurationField from './components/DurationField';
import ScenarioModeSection from './components/ScenarioModeSection';
import SettingsSection from './components/SettingsSection';
import { FIELD_HELP, makeDefaultSim } from './config/floodSimConfig';
import { fromLocalInputValue, nowLocalInputValue, toLocalInputValue } from './utils/datetime.utils';
import type {
  CatalogScenario,
  FloodSimSettingsProps,
  SimMonthlySource,
  SimSettings,
} from './FloodSimSettings.types';
import styles from './FloodSimSettings.module.css';

type ModeButtonStringProps = {
  options: string[];
  selectedOption?: string | null;
  onChange?: (value: string) => void;
  enabled?: boolean;
  buttonClassName?: string;
};
const TypedModeButton = ModeButton as ComponentType<ModeButtonStringProps>;

/**
 * Is the device demoing right now, as far as the database knows?
 *
 * `RI_Device.sim_mode` is what the simulator's loader selects on (0 off, 1
 * donor, 2 scenario). It says the device is CONFIGURED to demo; whether the
 * worker is actually ticking it is the ProjectDocs Flood Sim console's
 * business. Operator copy says "demo", never "simulated" or "real"
 * (utils/notifications/deploymentWording.utils.js).
 */
function statusLine(
  simMode: number | null | undefined,
  sim: SimSettings | null | undefined,
): { text: string; className: string } {
  const mode = Number(simMode ?? 0);
  if (mode === 2) {
    return {
      text: `Demo running — scenario "${sim?.scenario_name ?? '?'}"`,
      className: 'text-success',
    };
  }
  if (mode === 1) {
    return {
      text: `Demo running — donor replay${sim?.donor_uid != null ? ` of UID ${sim.donor_uid}` : ''}`,
      className: 'text-success',
    };
  }
  return { text: 'Demo off — this device writes no readings', className: 'text-theme-muted' };
}

export default function FloodSimSettings({
  sim,
  onChange,
  enabled,
  device,
  hasUnsavedChanges,
  onOpenDevice,
  pendingDownstream,
  onStageDownstream,
  onUnstageDownstream,
}: FloodSimSettingsProps) {
  const { data: catalog } = useGetStageSimScenariosQuery(undefined, { skip: !enabled });

  const base = (): SimSettings => sim ?? makeDefaultSim();

  const set = <K extends keyof SimSettings>(field: K, value: SimSettings[K]): void =>
    onChange({ ...base(), [field]: value });
  const setScenarioParam = (field: string, value: number): void =>
    onChange({
      ...base(),
      scenario_params: { ...base().scenario_params, [field]: value },
    });
  const setMonthlySource = (field: keyof SimMonthlySource, value: number | null): void =>
    onChange({
      ...base(),
      monthly_source: { ...(sim?.monthly_source ?? { year: null, month: null }), [field]: value },
    });
  const status = statusLine(device?.sim_mode, sim);
  const isDonor = sim?.sim_type === 'donor';
  const isScenario = sim?.sim_type === 'scenario';

  return (
    <BaseCard
      className={`${styles.card} shadow-sm`}
      bodyClassName="d-flex flex-column gap-3 p-3 p-lg-4"
    >
      <header
        className={`${styles.header} d-flex flex-column flex-lg-row gap-3 justify-content-between`}
      >
        <div>
          <div className={styles.eyebrow}>Advanced configuration</div>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
            <h3 className="h5 mb-0">Demo settings</h3>
            <span className="badge text-bg-warning">Admin</span>
          </div>
          <p className="text-theme-muted small mb-0 mt-2">
            Generate demo readings, replay historical data, and model waterway timing.
          </p>
        </div>
        <div
          className={`${styles.status} ${status.className}`}
          role="status"
          title="From RI_Device.sim_mode. Worker activity is shown on the ProjectDocs Flood Sim console."
        >
          {status.text}
        </div>
      </header>

      <SettingsSection
        title="Demo control"
        description="Turn the demo on for this device and choose how readings are generated."
      >
        <FloodSettingsField
          variant="toggle"
          label="Demo enabled"
          help={FIELD_HELP.enabled}
          labelClassName="fw-semibold"
          id="sim-enabled-toggle"
          checked={!!sim?.enabled}
          onChange={(event) => set('enabled', (event.target.checked ? 1 : 0) as 0 | 1)}
        />
        {sim?.enabled ? (
          <div className="row align-items-center g-2 flood-settings-field">
            <div className="col-lg-5">
              <Form.Label className="fw-semibold mb-0" title={FIELD_HELP.sim_type}>
                Demo type
              </Form.Label>
              <div className="text-theme-muted small mt-1">{FIELD_HELP.sim_type}</div>
            </div>
            <div className="col-lg-7">
              <TypedModeButton
                options={['donor', 'scenario']}
                selectedOption={sim?.sim_type}
                onChange={(value) => set('sim_type', value as 'donor' | 'scenario')}
                enabled={enabled}
                buttonClassName="text-capitalize"
              />
            </div>
          </div>
        ) : null}
      </SettingsSection>

      {sim?.enabled ? (
        <>
          {/* A field only exists in the mode that reads it: Loop under donor
              (donor replay is its one reader), the tick cadences under scenario
              (donor replay keeps the history's own spacing). Demo End Time is
              shared — the simulator honours it in both modes. */}
          <SettingsSection
            title="Timing & playback"
            description="Set when the demo stops, how often it ticks, and whether it repeats."
          >
            <FloodSettingsField
              label="Demo end time"
              help={FIELD_HELP.sim_end_time}
              type="datetime-local"
              min={nowLocalInputValue()}
              value={toLocalInputValue(sim?.sim_end_time)}
              onChange={(event) => set('sim_end_time', fromLocalInputValue(event.target.value))}
            />
            {isDonor ? (
              <FloodSettingsField
                variant="toggle"
                label="Loop replay"
                help={FIELD_HELP.loop_enabled}
                id="sim-loop-toggle"
                checked={!!sim?.loop_enabled}
                onChange={(event) => set('loop_enabled', (event.target.checked ? 1 : 0) as 0 | 1)}
              />
            ) : null}
            {isScenario ? (
              <>
                <DurationField
                  label="Interval"
                  help={FIELD_HELP.interval_ms}
                  ms={sim?.interval_ms}
                  minMs={10_000}
                  onMsChange={(ms) => set('interval_ms', ms)}
                />
                <DurationField
                  label="Event interval"
                  help={FIELD_HELP.event_interval_ms}
                  ms={sim?.event_interval_ms}
                  minMs={1_000}
                  onMsChange={(ms) => set('event_interval_ms', ms)}
                />
              </>
            ) : null}
          </SettingsSection>
          {isDonor ? (
            <DonorModeSection sim={sim} set={set} setMonthlySource={setMonthlySource} />
          ) : null}
          {isScenario ? (
            <ScenarioModeSection
              sim={sim}
              set={set}
              setScenarioParam={setScenarioParam}
              onChange={onChange}
              catalog={catalog as CatalogScenario[] | undefined}
              device={device}
              hasUnsavedChanges={hasUnsavedChanges}
              onOpenDevice={onOpenDevice}
              pendingDownstream={pendingDownstream}
              onStageDownstream={onStageDownstream}
              onUnstageDownstream={onUnstageDownstream}
            />
          ) : null}
        </>
      ) : (
        <div className={styles['disabled-state']}>
          Turn the demo on to configure playback, data sources, scenarios, and waterway routing.
        </div>
      )}
    </BaseCard>
  );
}
